import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, MessageSquare, Star, Quote } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    content: "Square Trigger transformed our outdated website into a modern, high-performing platform. Our online inquiries increased by 200% within the first three months. Their team truly understands how to deliver results.",
    author: "Sarah Mitchell",
    role: "CEO",
    company: "Mitchell & Associates",
    rating: 5,
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
  },
  {
    id: 2,
    content: "The SEO strategy Square Trigger implemented helped us rank on the first page of Google for our key terms. We've seen consistent growth in organic traffic and qualified leads. Highly recommend their services!",
    author: "David Chen",
    role: "Marketing Director",
    company: "TechFlow Solutions",
    rating: 5,
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
  },
  {
    id: 3,
    content: "Working with Square Trigger was a game-changer for our e-commerce business. They redesigned our entire shopping experience and our conversion rate improved by 150%. Professional, responsive, and truly talented.",
    author: "Emily Rodriguez",
    role: "Founder",
    company: "Artisan Goods Co.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop"
  },
  {
    id: 4,
    content: "The custom dashboard Square Trigger built for us has revolutionized how we manage our operations. Their attention to detail and understanding of our needs was exceptional. A truly world-class team.",
    author: "Michael Thompson",
    role: "Operations Manager",
    company: "Global Logistics Inc.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
  },
  {
    id: 5,
    content: "From the initial consultation to the final launch, Square Trigger exceeded our expectations at every step. Their expertise in UI/UX design resulted in a website that our customers love.",
    author: "Jennifer Lee",
    role: "Product Manager",
    company: "InnovateTech",
    rating: 5,
    image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop"
  },
  {
    id: 6,
    content: "Square Trigger's social media marketing strategies helped us build a strong online community. Our engagement rates have tripled and we're seeing real business results from our social presence.",
    author: "Robert Martinez",
    role: "Brand Manager",
    company: "Lifestyle Brands",
    rating: 5,
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
  }
];

export default function TestimonialsPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="secondary" className="mb-6">
              <MessageSquare className="h-4 w-4 mr-2" />
              Testimonials
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              What Our{' '}
              <span className="gradient-text">Clients Say</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Don't just take our word for it. Here's what our clients have to say about working with Square Trigger.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover flex flex-col">
                  {/* Quote Icon */}
                  <div className="w-10 h-10 rounded-lg gradient-primary-soft flex items-center justify-center mb-4">
                    <Quote className="h-5 w-5 text-primary" />
                  </div>

                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-warning text-warning" />
                    ))}
                  </div>

                  {/* Content */}
                  <p className="text-foreground leading-relaxed mb-6 flex-grow">
                    "{testimonial.content}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-3 pt-4 border-t border-border mt-auto">
                    <img
                      src={testimonial.image}
                      alt={testimonial.author}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-semibold text-foreground">{testimonial.author}</h4>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Ready to Join Our Happy Clients?
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Let's create success together.
            </p>
            <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90">
              <Link to="/get-started">
                Start Your Project
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
